import cx from 'classnames';
import type { ComponentProps, ComponentType, ElementType, PropsWithChildren, ReactNode } from 'react';
import { createContext } from 'react';
import type { Except } from 'type-fest';
import { deprecate } from '../../utils/deprecate';
import type { IconProps } from '../Icon';
import { Icon } from '../Icon';
import type { DefaultLinkImplementationProps, LinkImplementationRequiredProps, LinkProps } from '../Link';
import { Link } from '../Link';
import types from './styles.types.module.css';
import variants from './styles.variants.module.css';

type TypographyHeaderType = `header${'1' | '2' | '3' | '4' | '5'}`;
type TypographyBodyType = 'body' | `body-${'large' | 'medium' | 'small' | 'xsmall'}`;
type TypographyType = TypographyHeaderType | TypographyBodyType;

type TypographyVariant = 'regular' | 'bold';
type TypographyColor =
  | 'black'
  | 'black-anthracite'
  | 'black-light'
  | 'white'
  | 'white-light'
  | 'primary'
  | 'accent'
  | 'success'
  | 'danger';

const variantsStyles: Readonly<Record<`${TypographyColor}-${TypographyVariant}`, string>> = variants;

const ColorContext = createContext(undefined as unknown as TypographyColor);

interface TypographyTagBaseProps {
  className?: string;
}

export interface TypographyProps<P extends TypographyTagBaseProps = TypographyTagBaseProps> {
  tag: ElementType<P>;
  color?: TypographyColor;
  variant?: TypographyVariant;
  base?: TypographyType;
  small?: TypographyType;
  medium?: TypographyType;
  large?: TypographyType;
}

function InternalTypography<P extends TypographyTagBaseProps = TypographyTagBaseProps>({
  tag,
  className,
  color,
  variant = 'regular',
  base,
  small,
  medium = __DEV__ ? undefined : small || base,
  large,
  ...props
}: TypographyProps<P> & P): ReactNode {
  if (__DEV__) {
    if (base && !types[`base-${base}`]) {
      throw new Error(`Missing classname for base-${base}. ${Object.keys(types).join(', ')}.`);
    }

    if (!color) {
      throw new Error('color missing');
    }

    // @ts-expect-error variant light is deprecated
    if (variant === 'light') {
      throw new Error('"light" variant is not available, use black-light or white-light instead');
    }

    if (large && large === medium) {
      throw new Error('No need to set the large prop if it is the same as medium !');
    }

    if (medium && medium === (small || base)) {
      throw new Error(`No need to set the medium prop if it is the same as ${small ? 'small' : 'base'} !`);
    }

    if (small && small === base) {
      throw new Error('No need to set the small prop if it is the same as base !');
    }

    // eslint-disable-next-line no-param-reassign
    if (!medium) medium = small || base;
  }

  const Tag = tag as ComponentType<P>;

  return (
    <Tag
      {...(props as any)}
      className={cx(
        types.Reset,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        variantsStyles[`${color!}-${variant}` as const],
        base && (types as any)[`base-${base}`],
        small && (types as any)[`small-${small}`],
        medium && (types as any)[`medium-${medium}`],
        large && (types as any)[`large-${large}`],
        className,
      )}
    />
  );
}

// @types/react currently don't consider children is part of the props
/** @deprecated use Typography from kitt-universal instead */
export function Typography<P extends PropsWithChildren<TypographyTagBaseProps>>(
  props: TypographyProps<P> & P,
): ReactNode {
  if (__DEV__) {
    const keys = Object.keys(props);
    const order = ['base', 'small', 'medium', 'large'];
    order.forEach((prop, index) => {
      if (index === 0 || !keys.includes(prop)) return;
      order.slice(0, index).forEach((propBefore) => {
        if (keys.includes(propBefore) !== undefined && keys.indexOf(propBefore) > keys.indexOf(prop)) {
          throw new Error(`prop ${prop} must be placed after ${propBefore}`);
        }
      });
    });
    Object.entries(props).forEach(([key, value], index) => {
      if (!order.includes(key)) return;
      if (props.variant === 'regular' && value?.startsWith('header')) {
        deprecate(
          'Typography',
          `variant="${props.variant}" ${key}="${
            value as string
          }" using variant with header style is deprecated, use "body-large" or "body-medium".`,
        );
      }
    });
  }

  if (props.color) {
    // use the color and pass the color to the context for children
    return (
      <ColorContext.Provider value={props.color}>
        <InternalTypography {...props} />
      </ColorContext.Provider>
    );
  }

  // use parent color if defined or default to black
  return (
    <ColorContext.Consumer>
      {(colorInContext) => <InternalTypography {...props} color={colorInContext || 'black'} />}
    </ColorContext.Consumer>
  );
}

type TypographyWithoutTagProps<P extends TypographyTagBaseProps> = Except<TypographyProps<P>, 'tag'>;

interface CreateForTagConfig<Tag extends ElementType<TypographyTagBaseProps>> {
  tag: Tag;
  defaultType?: TypographyType;
  smallType?: TypographyType;
  mediumType?: TypographyType;
}

function createForTag<Tag extends ElementType<TypographyTagBaseProps>>({
  tag,
  defaultType,
  smallType,
  mediumType,
}: CreateForTagConfig<Tag>): ComponentType<
  TypographyWithoutTagProps<ComponentProps<Tag>> & PropsWithChildren<ComponentProps<Tag>>
> {
  function TypographyWithTag({
    base = defaultType,
    small = smallType,
    medium = mediumType,
    ...props
  }: TypographyWithoutTagProps<ComponentProps<Tag>> & PropsWithChildren<ComponentProps<Tag>>): ReactNode {
    return <Typography tag={tag} base={base} small={small} medium={medium} {...props} />;
  }
  if (__DEV__) {
    TypographyWithTag.displayName = `Typography.${String(tag)}`;
  }
  return TypographyWithTag;
}

Typography.h1 = createForTag({ tag: 'h1', defaultType: 'header1' });
Typography.h2 = createForTag({ tag: 'h2', defaultType: 'header2' });
Typography.h3 = createForTag({ tag: 'h3', defaultType: 'header3' });
Typography.h4 = createForTag({ tag: 'h4', defaultType: 'header4' });
Typography.h5 = createForTag({ tag: 'h5', defaultType: 'body', mediumType: 'body-medium' });
Typography.div = createForTag({ tag: 'div' });
Typography.span = createForTag({ tag: 'span' });
Typography.Text = createForTag({ tag: 'span' });
Typography.p = createForTag({ tag: 'p' });
Typography.section = createForTag({ tag: 'section' });

export type TypographyLinkProps<P extends LinkImplementationRequiredProps> = TypographyWithoutTagProps<LinkProps<P>> &
  LinkProps<P>;

function TypographyLink<P extends LinkImplementationRequiredProps = DefaultLinkImplementationProps>({
  color,
  variant = 'bold',
  ...props
}: TypographyLinkProps<P>): ReactNode {
  return <Typography tag={Link} color={color} variant={variant === 'regular' ? undefined : variant} {...props} />;
}

Typography.Link = TypographyLink;

interface TypographyIconProps extends Except<IconProps, 'color'>, TypographyTagBaseProps {
  color?: TypographyColor;
  variant?: TypographyVariant;
}

function TypographyIcon(props: TypographyIconProps): ReactNode {
  return <Typography tag={Icon} {...props} />;
}

Typography.Icon = TypographyIcon;
